import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, pct, biasColor, marketLabel } from "@/lib/format";
import { DashboardLayout } from "@/components/DashboardLayout";
import { PriceChart } from "@/components/PriceChart";
import { DataStatusBadge } from "@/components/DataStatusBadge";
import { SignalCard } from "@/components/SignalCard";

const TFS = ["15m", "1h", "4h", "1D"];

export default function AppMarkets() {
  const [assets, setAssets] = useState([]);
  const [sel, setSel] = useState("BTC/USD");
  const [tf, setTf] = useState("4h");
  const [tab, setTab] = useState("all");
  const [snap, setSnap] = useState(null);
  const [candles, setCandles] = useState([]);
  const [mtf, setMtf] = useState(null);
  const [signal, setSignal] = useState(null);

  useEffect(() => { api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);

  useEffect(() => {
    const enc = encodeURIComponent(sel);
    api.get(`/markets/${enc}/snapshot?timeframe=${tf}`).then((r) => setSnap(r.data)).catch(() => {});
    api.get(`/markets/${enc}/candles?timeframe=${tf}&bars=150`).then((r) => setCandles(r.data.candles)).catch(() => {});
    api.get(`/markets/${enc}/mtf`).then((r) => setMtf(r.data)).catch(() => {});
    api.get(`/signals/${enc}?timeframe=${tf}`).then((r) => setSignal(r.data)).catch(() => {});
  }, [sel, tf]);

  const rows = assets.filter((a) => tab === "all" || a.market === tab);

  return (
    <DashboardLayout title="Markets">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Asset list */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {["all", "crypto", "forex", "stocks", "indices", "commodities"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`app-market-tab-${t}`} className={`rounded-full px-2.5 py-1 text-xs capitalize ${tab === t ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{t === "all" ? "All" : marketLabel[t]}</button>
            ))}
          </div>
          <div className="max-h-[520px] space-y-1 overflow-y-auto no-scrollbar">
            {rows.map((a) => (
              <button key={a.symbol} onClick={() => setSel(a.symbol)} data-testid={`app-asset-${a.symbol.replace(/\W/g, "-").toLowerCase()}`}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left ${sel === a.symbol ? "bg-primary/10" : "hover:bg-secondary/50"}`}>
                <div><div className="font-display text-sm font-semibold">{a.symbol}</div><div className={`text-[10px] uppercase ${biasColor(a.ai_bias)}`}>{a.ai_bias}</div></div>
                <div className="text-right"><div className="font-mono text-xs">{fmt(a.price)}</div><div className={`text-[10px] ${a.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(a.change_pct)}</div></div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><h2 className="font-display text-2xl font-bold">{sel}</h2><DataStatusBadge status={snap?.data_status || "DEMO"} /></div>
                {snap && <div className="mt-1 flex items-center gap-3"><span className="font-mono text-xl">{fmt(snap.price)}</span><span className={`text-sm ${snap.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(snap.change_pct)}</span></div>}
              </div>
              <div className="flex gap-1">
                {TFS.map((t) => <button key={t} onClick={() => setTf(t)} data-testid={`tf-${t}`} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${tf === t ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{t}</button>)}
              </div>
            </div>
            <div className="mt-4"><PriceChart candles={candles} height={260} /></div>
            {snap && (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Metric label="RSI" value={fmt(snap.rsi, 1)} />
                <Metric label="MACD hist" value={fmt(snap.macd.hist, 3)} />
                <Metric label="EMA 20" value={fmt(snap.ema20)} />
                <Metric label="EMA 50" value={fmt(snap.ema50)} />
                <Metric label="ATR" value={fmt(snap.atr, 3)} />
                <Metric label="Support" value={fmt(snap.support)} />
                <Metric label="Resistance" value={fmt(snap.resistance)} />
                <Metric label="Volatility" value={`${snap.volatility} (${snap.volatility_pct}%)`} />
              </div>
            )}
          </div>

          {mtf && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Multi-Timeframe Alignment</h3><span className={`rounded-md px-2 py-0.5 text-xs font-semibold uppercase ${mtf.alignment === "STRONG" ? "bg-bullish/15 text-bullish" : mtf.alignment === "MODERATE" ? "bg-warning/15 text-warning" : "bg-secondary text-muted-foreground"}`}>{mtf.alignment}</span></div>
              <div className="mt-4 grid grid-cols-4 gap-3">
                {mtf.rows.map((r) => (
                  <div key={r.timeframe} className="rounded-xl border border-border p-3 text-center">
                    <div className="text-xs text-muted-foreground">{r.timeframe}</div>
                    <div className={`mt-1 text-sm font-semibold capitalize ${biasColor(r.bias)}`}>{r.bias}</div>
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">RSI {fmt(r.rsi, 0)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {signal && <div><h3 className="mb-3 font-display text-lg font-semibold">Latest AI Signal</h3><SignalCard s={signal} testid="market-detail-signal" /></div>}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Metric({ label, value }) {
  return <div className="rounded-xl border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 font-mono text-sm font-semibold capitalize">{value}</div></div>;
}
